// Hideo - script editor's writing surface: the pieces every part uses. The page (#scriptBody, static markup in
// index.html, never drawn by Vue), the selection kept for the toolbar's commands (savedRange), the caret, the top-level
// block of a node, the list under the cursor, and ex() (a browser editing command on the page). Ported from the legacy
// editor/editor.js with the same steps (golden tests: tests/editor-surface*.test.ts against
// tests/fixtures/legacy/editor-w11.js, and the Chromium comparison described in docs/migration.md).
import { useEditorStore } from '../../../stores/editor'
import { edBodyEl } from '../html'

/** An element of the static markup */
export const byId = (id: string) => document.getElementById(id) as HTMLElement
/** The page */
export const edBody = edBodyEl
/** The element of a selection point: a text node's parent, or the node itself */
export const elOf = (n: Node | null | undefined): Element | null => (!n ? null : n.nodeType === 3 ? n.parentElement : n as Element)
/** A new empty block (an element with a <br>, so the cursor can go in it): a paragraph unless said otherwise */
export function emptyBlock(tag = 'p'): HTMLElement { const p = document.createElement(tag); p.innerHTML = '<br>'; return p }
/** Kind of a list: 'ol', 'todo' (checklist) or 'ul' */
export const listKind = (l: Element): 'ol' | 'todo' | 'ul' => (l.tagName === 'OL' ? 'ol' : l.classList.contains('todo') ? 'todo' : 'ul')
/** The selection's range when it is inside the page (notEmpty: only when some text is selected), else null */
export function pageRange(notEmpty = false): Range | null {
  const s = getSelection()
  if (!s || !s.rangeCount || (notEmpty && s.isCollapsed)) return null
  const r = s.getRangeAt(0)
  return edBody().contains(r.commonAncestorContainer) ? r : null
}

/** The page's selection kept for the toolbar's commands (null = none): the toolbar and its menus take the focus away */
export let savedRange: Range | null = null
export function setSavedRange(r: Range | null): void { savedRange = r }

/** The text changed: saved 600 ms later (editor store; read at every call, so tests can spy on the store) */
export const queueScriptSave = () => useEditorStore().queueSave()

/** Puts back the kept selection */
export function restoreSel(): void { if (savedRange) { const s = getSelection() as Selection; s.removeAllRanges(); s.addRange(savedRange) } }
/** A browser editing command on the page, with the kept selection, then saved */
export function ex(c: string, a?: string | null): void { edBody().focus(); restoreSel(); document.execCommand(c, false, a ?? null as unknown as string); queueScriptSave() }

/** Top-level block (direct child of the page) containing the node */
export function edBlockOf(n: Node | null): HTMLElement | null {
  const body = edBody()
  while (n && n.parentNode !== body) { if (n === body || !n.parentNode) return null; n = n.parentNode }
  return n && n.nodeType === 1 ? n as HTMLElement : null
}
/** Puts the cursor in a node of the page (and keeps it as the selection) */
export function edCaret(node: Node, off: number): void {
  const r = document.createRange(); r.setStart(node, off); r.collapse(true)
  const s = getSelection() as Selection; s.removeAllRanges(); s.addRange(r); savedRange = r.cloneRange()
}
/** The list (ul / ol) of the page where the selection starts, or null */
export const selList = (): HTMLElement | null => {
  const s = getSelection() as Selection, n = s.rangeCount && s.anchorNode, el = n && elOf(n)
  const l = el && el.closest && el.closest<HTMLElement>('ul,ol')
  return l && edBody().contains(l) ? l : null
}
